// ============================================================
// Datei: frontend/src/pages/Dashboard.tsx
// ============================================================
import React, { useEffect, useState, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, BarChart, Bar, Cell,
} from 'recharts';
import {
  metricsApi, accountsApi, analyticsApi,
  Account, TradingMetrics, AnalyticsReport, AlgorithmDiscovery,
} from '../services/api';
import BacktestTab from '../components/BacktestTab';
import './Dashboard.css';

// ─────────────────────────────────────────────
// Metric Card
// ─────────────────────────────────────────────
interface MetricCardProps {
  label: string;
  value: string;
  sub?: string;
  positive?: boolean;
  negative?: boolean;
  accent?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, sub, positive, negative, accent }) => (
  <div className={`metric-card${positive ? ' metric-positive' : ''}${negative ? ' metric-negative' : ''}${accent ? ' metric-accent' : ''}`}>
    <h3>{label}</h3>
    <div className="metric-value">{value}</div>
    {sub && <div className="metric-sub">{sub}</div>}
  </div>
);

// ─────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const [activeAccount, setActiveAccount]   = useState<Account | null>(null);
  const [metrics, setMetrics]               = useState<TradingMetrics | null>(null);
  const [equityCurve, setEquityCurve]       = useState<any[]>([]);
  const [drawdownData, setDrawdownData]     = useState<any[]>([]);
  const [sharpeData, setSharpeData]         = useState<any>({ rolling_sharpe: [], modified_sharpe: [] });
  const [analytics, setAnalytics]           = useState<AnalyticsReport | null>(null);
  const [algorithm, setAlgorithm]           = useState<AlgorithmDiscovery | null>(null);
  const [loading, setLoading]               = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [trades, setTrades] = useState<any[]>([]);
  const [tab, setTab]                       = useState<'overview' | 'backtest' | 'intelligence' | 'ml'>('overview');

  const loadData = useCallback(async () => {
    try {
      const accountsRes = await accountsApi.list();
      const active = accountsRes.data.find(a => a.is_active);
      if (!active) { setLoading(false); return; }
      setActiveAccount(active);

      const [mRes, eRes, dRes, sRes] = await Promise.all([
        metricsApi.getMetrics(active.id),
        metricsApi.getEquityCurve(active.id),
        metricsApi.getDrawdown(active.id),
        metricsApi.getSharpe(active.id),
      ]);
      setMetrics(mRes.data);
      setEquityCurve(eRes.data);
      setDrawdownData(dRes.data);
      setSharpeData(sRes.data);
      setLoading(false);

      // Trades für Histogram laden
      try { const tRes = await import('../services/api').then(m => m.tradesApi.list({ account_id: active.id })); setTrades(tRes.data); } catch {}

      // Analytics im Hintergrund laden
      setAnalyticsLoading(true);
      try {
        const [aRes, algoRes] = await Promise.all([
          analyticsApi.getReport(active.id),
          analyticsApi.getAlgorithmDiscovery(active.id),
        ]);
        setAnalytics(aRes.data);
        setAlgorithm(algoRes.data);
      } catch { /* < 5 Trades — kein Error */ }
      finally { setAnalyticsLoading(false); }

    } catch (e) {
      console.error('Dashboard load error:', e);
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state">
          <div className="loading-spinner" />
          <span>Lade Metriken...</span>
        </div>
      </div>
    );
  }

  if (!activeAccount || !metrics) {
    return (
      <div className="dashboard">
        <h1>Trading Engine</h1>
        <div className="empty-state">
          <p>Kein aktives Konto gefunden. Aktiviere ein Konto und füge Trades hinzu.</p>
        </div>
      </div>
    );
  }

  const rrSign = (metrics.expectancy_r ?? 0) >= 0 ? '+' : '';

  return (
    <div className="dashboard">

      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>Trading Engine</h1>
          <span className="dashboard-account">{activeAccount.name}</span>
        </div>
        <button className="btn-refresh" onClick={loadData}>↻ Refresh</button>
      </div>

      {/* Tabs */}
      <div className="tab-nav">
        {([
          ['overview',     '📈 Overview'],
          ['backtest',     '📊 Backtest'],
          ['intelligence', '🧠 Metric Intelligence'],
          ['ml',           '🤖 ML & Algo Discovery'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            className={`tab-btn${tab === key ? ' tab-btn-active' : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ══ OVERVIEW ══ */}
      {tab === 'overview' && (
        <>
          <div className="metrics-grid">
            <MetricCard label="Gesamt Trades"   value={`${metrics.total_trades}`} />
            <MetricCard
              label="Win Rate"
              value={`${metrics.win_rate.toFixed(1)}%`}
              positive={metrics.win_rate >= 50}
              negative={metrics.win_rate < 50}
            />
            <MetricCard
              label="Expectancy"
              value={`${rrSign}${(metrics.expectancy_r ?? 0).toFixed(2)}R`}
              sub={`$${(metrics.expectancy ?? 0).toFixed(2)} pro Trade`}
              positive={metrics.expectancy_r >= 0}
              negative={metrics.expectancy_r < 0}
            />
            <MetricCard
              label="Ø RR"
              value={(metrics.avg_rr ?? 0) > 0 ? (metrics.avg_rr ?? 0).toFixed(2) : '—'}
              sub={`${metrics.trades_with_rr ?? 0} von ${metrics.total_trades} Trades mit RR`}
              positive={(metrics.avg_rr ?? 0) >= 1.5}
            />
            <MetricCard
              label="Ø RR Wins"
              value={(metrics.avg_rr_wins ?? 0) > 0 ? (metrics.avg_rr_wins ?? 0).toFixed(2) : '—'}
              sub="Gewinner-Trades"
              positive
            />
            <MetricCard
              label="Ø RR Losses"
              value={(metrics.avg_rr_losses ?? 0) > 0 ? (metrics.avg_rr_losses ?? 0).toFixed(2) : '—'}
              sub="Verlierer-Trades"
              negative
            />
            <MetricCard
              label="Sharpe Ratio"
              value={metrics.sharpe_ratio.toFixed(2)}
              positive={metrics.sharpe_ratio >= 1}
              negative={metrics.sharpe_ratio < 0}
            />
            <MetricCard label="Modified Sharpe" value={metrics.modified_sharpe.toFixed(2)} />
            <MetricCard
              label="Max Drawdown"
              value={`${metrics.max_drawdown.toFixed(2)}%`}
              negative
            />
            <MetricCard
              label="DD Duration"
              value={`${metrics.max_drawdown_duration ?? 0} min`}
              sub="Zeit im Drawdown"
              negative={(metrics.max_drawdown_duration ?? 0) > 240}
            />
            <MetricCard label="Ø Win"  value={`$${metrics.avg_win.toFixed(2)}`}  positive />
            <MetricCard label="Ø Loss" value={`$${metrics.avg_loss.toFixed(2)}`} negative />
            <MetricCard
              label="Net PnL"
              value={`$${metrics.net_pnl.toFixed(2)}`}
              sub={`${metrics.total_return_pct.toFixed(2)}% Return`}
              positive={metrics.net_pnl >= 0}
              negative={metrics.net_pnl < 0}
            />
            <MetricCard
              label="Profit Factor"
              value={(metrics.profit_factor ?? 0) > 0 ? (metrics.profit_factor ?? 0).toFixed(2) : '—'}
              sub="Brutto-Gewinne / Brutto-Verluste"
              positive={(metrics.profit_factor ?? 0) >= 1.5}
              negative={(metrics.profit_factor ?? 0) < 1.0 && (metrics.profit_factor ?? 0) > 0}
            />
            <MetricCard
              label="Payoff Ratio"
              value={(metrics.payoff_ratio ?? 0) > 0 ? (metrics.payoff_ratio ?? 0).toFixed(2) : '—'}
              sub="Avg Win / |Avg Loss|"
              positive={(metrics.payoff_ratio ?? 0) >= 2.0}
            />
            <MetricCard
              label="Kelly %"
              value={`${((metrics.kelly_criterion ?? 0) * 100).toFixed(1)}%`}
              sub="Optimale Positionsgröße"
              positive={(metrics.kelly_criterion ?? 0) > 0}
              negative={(metrics.kelly_criterion ?? 0) < 0}
            />
            <MetricCard
              label="Calmar Ratio"
              value={(metrics.calmar_ratio ?? 0).toFixed(2)}
              sub="Return / Max Drawdown"
              positive={(metrics.calmar_ratio ?? 0) >= 1.0}
              negative={(metrics.calmar_ratio ?? 0) < 0.5}
            />
            <MetricCard
              label="Recovery Factor"
              value={(metrics.recovery_factor ?? 0).toFixed(2)}
              sub="Net PnL / Max DD ($)"
              positive={(metrics.recovery_factor ?? 0) >= 1.0}
              negative={(metrics.recovery_factor ?? 0) < 0}
            />
            <MetricCard
              label="Aktuelle Equity"
              value={`$${metrics.current_equity.toFixed(2)}`}
              sub={`Start: $${metrics.start_capital.toFixed(2)}`}
              accent
            />
          </div>

          <div className="chart-container">
            <h2>Equity Curve</h2>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={equityCurve}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
                <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }} />
                <Legend />
                <Line type="monotone" dataKey="equity" stroke="#22c55e" strokeWidth={2} dot={false} name="Equity ($)" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container">
            <h2>Drawdown %</h2>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={drawdownData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
                <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
                  formatter={(v: any) => [`${Number(v).toFixed(2)}%`, 'Drawdown']}
                />
                <Line type="monotone" dataKey="drawdown_pct" stroke="#ef4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* PnL Distribution Histogram */}
          <div className="chart-container">
            <h2>PnL Verteilung</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={(() => {
                if (!equityCurve.length) return [];
                const pnls = trades.map((t: any) => t.pnl);
                if (!pnls.length) return [];
                const min = Math.floor(Math.min(...pnls) / 50) * 50;
                const max = Math.ceil(Math.max(...pnls) / 50) * 50;
                const buckets: {range: string; count: number; color: string}[] = [];
                for (let i = min; i < max; i += 50) {
                  const count = pnls.filter((p: number) => p >= i && p < i + 50).length;
                  if (count > 0) buckets.push({
                    range: `${i}`,
                    count,
                    color: i >= 0 ? '#22c55e' : '#ef4444'
                  });
                }
                return buckets;
              })()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
                <XAxis dataKey="range" tick={{ fill: '#64748b', fontSize: 10 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
                  formatter={(v: any) => [`${v} Trades`, 'Anzahl']}
                  labelFormatter={(l: any) => `$${l} bis $${Number(l)+50}`}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {(() => {
                    const pnls = trades.map((t: any) => t.pnl);
                    if (!pnls.length) return null;
                    const min = Math.floor(Math.min(...pnls) / 50) * 50;
                    const max = Math.ceil(Math.max(...pnls) / 50) * 50;
                    const cells = [];
                    for (let i = min; i < max; i += 50) {
                      const count = pnls.filter((p: number) => p >= i && p < i + 50).length;
                      if (count > 0) cells.push(
                        <Cell key={i} fill={i >= 0 ? '#22c55e' : '#ef4444'} />
                      );
                    }
                    return cells;
                  })()}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

                    {sharpeData.rolling_sharpe.length > 0 && (
            <div className="charts-row">
              <div className="chart-container">
                <h2>Rolling Sharpe (10 Trades)</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={sharpeData.rolling_sharpe}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
                    <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }} />
                    <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={false} name="Sharpe" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-container">
                <h2>Rolling Modified Sharpe</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={sharpeData.modified_sharpe}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
                    <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }} />
                    <Line type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2} dot={false} name="Mod. Sharpe" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══ BACKTEST ══ */}
      {tab === 'backtest' && <BacktestTab accountId={activeAccount.id} />}

      {/* ══ METRIC INTELLIGENCE ══ */}
      {tab === 'intelligence' && (
        <>
          {analyticsLoading && (
            <div className="loading-state"><div className="loading-spinner" /> Analysiere...</div>
          )}
          {!analyticsLoading && !analytics && (
            <div className="empty-state"><p>Mindestens 5 Trades nötig für Metric Intelligence.</p></div>
          )}
          {analytics && (
            <>
              <div className="section-title">🧠 AI Insights <span className="count-badge">{analytics.insights.length}</span></div>
              <div className="insights-list">
                {analytics.insights.map((ins, i) => (
                  <div key={i} className={`insight-card insight-${ins.severity}`}>
                    <div className="insight-icon">
                      {ins.severity === 'positive' ? '↑' : ins.severity === 'negative' ? '↓' : ins.severity === 'warning' ? '⚠' : 'ℹ'}
                    </div>
                    <div>
                      <p className="insight-msg">{ins.message}</p>
                      <span className="insight-meta">{ins.category} · {Math.round(ins.confidence * 100)}% Konfidenz</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="section-title">📅 Session Performance</div>
              <div className="session-grid">
                {analytics.session_stats.map(s => (
                  <div key={s.session} className="session-card">
                    <div className="session-name">{s.session}</div>
                    <div className="session-row">
                      <span>Win Rate</span>
                      <strong className={s.win_rate >= 50 ? 'text-green' : 'text-red'}>
                        {s.win_rate.toFixed(1)}%
                      </strong>
                    </div>
                    <div className="session-row"><span>Avg PnL</span>
                      <strong className={s.avg_pnl >= 0 ? 'text-green' : 'text-red'}>
                        ${s.avg_pnl.toFixed(2)}
                      </strong>
                    </div>
                    <div className="session-row"><span>Avg RR</span>
                      <strong>{s.avg_rr > 0 ? s.avg_rr.toFixed(2) : '—'}</strong>
                    </div>
                    <div className="session-row"><span>Trades</span><strong>{s.total_trades}</strong></div>
                    <div className="session-row"><span>Total PnL</span>
                      <strong className={s.total_pnl >= 0 ? 'text-green' : 'text-red'}>
                        ${s.total_pnl.toFixed(2)}
                      </strong>
                    </div>
                  </div>
                ))}
              </div>

              {analytics.session_stats.length > 0 && (
                <div className="chart-container">
                  <h2>Session Win Rate</h2>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={analytics.session_stats}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
                      <XAxis dataKey="session" tick={{ fill: '#64748b' }} />
                      <YAxis domain={[0, 100]} tick={{ fill: '#64748b' }} />
                      <Tooltip
                        contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
                        formatter={(v: any) => [`${Number(v).toFixed(1)}%`, 'Win Rate']}
                      />
                      <Bar dataKey="win_rate" radius={[6, 6, 0, 0]}>
                        {analytics.session_stats.map((s, i) => (
                          <Cell key={i} fill={s.win_rate >= 50 ? '#22c55e' : '#ef4444'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="section-title">🔥 Streak Analyse</div>
              <div className="metrics-grid">
                <MetricCard label="Max Win Streak"  value={`${analytics.streak_analysis.max_win_streak}`}  positive />
                <MetricCard label="Max Loss Streak" value={`${analytics.streak_analysis.max_loss_streak}`} negative />
                <MetricCard
                  label="WR nach 3 Wins"
                  value={analytics.streak_analysis.win_rate_after_3_wins > 0
                    ? `${analytics.streak_analysis.win_rate_after_3_wins.toFixed(1)}%` : '—'}
                  sub="Overconfidence Check"
                  positive={analytics.streak_analysis.win_rate_after_3_wins >= 50}
                  negative={analytics.streak_analysis.win_rate_after_3_wins < 50 && analytics.streak_analysis.win_rate_after_3_wins > 0}
                />
                <MetricCard
                  label="WR nach 3 Losses"
                  value={analytics.streak_analysis.win_rate_after_3_losses > 0
                    ? `${analytics.streak_analysis.win_rate_after_3_losses.toFixed(1)}%` : '—'}
                  sub="Recovery Rate"
                  positive={analytics.streak_analysis.win_rate_after_3_losses >= 50}
                />
              </div>

              {analytics.rr_correlation && (
                <>
                  <div className="section-title">⚖️ RR Korrelation</div>
                  <div className="metrics-grid">
                    <MetricCard
                      label={`WR (RR ≥ ${analytics.rr_correlation.threshold})`}
                      value={`${analytics.rr_correlation.high_rr_win_rate.toFixed(1)}%`}
                      positive={analytics.rr_correlation.high_rr_win_rate >= 50}
                      negative={analytics.rr_correlation.high_rr_win_rate < 50}
                    />
                    <MetricCard
                      label={`WR (RR < ${analytics.rr_correlation.threshold})`}
                      value={`${analytics.rr_correlation.low_rr_win_rate.toFixed(1)}%`}
                      positive={analytics.rr_correlation.low_rr_win_rate >= 50}
                      negative={analytics.rr_correlation.low_rr_win_rate < 50}
                    />
                    <MetricCard
                      label="Hoher-RR PnL"
                      value={`$${analytics.rr_correlation.high_rr_pnl.toFixed(2)}`}
                      positive={analytics.rr_correlation.high_rr_pnl >= 0}
                      negative={analytics.rr_correlation.high_rr_pnl < 0}
                    />
                    <MetricCard
                      label="Niedriger-RR PnL"
                      value={`$${analytics.rr_correlation.low_rr_pnl.toFixed(2)}`}
                      positive={analytics.rr_correlation.low_rr_pnl >= 0}
                      negative={analytics.rr_correlation.low_rr_pnl < 0}
                    />
                  </div>
                </>
              )}

              <div className="section-title">⏱ Dauer Analyse</div>
              <div className="metrics-grid">
                <MetricCard
                  label={`Short WR (< ${analytics.duration_analysis.median_duration_mins.toFixed(0)} min)`}
                  value={`${analytics.duration_analysis.short_trade_win_rate.toFixed(1)}%`}
                  positive={analytics.duration_analysis.short_trade_win_rate >= 50}
                  negative={analytics.duration_analysis.short_trade_win_rate < 50}
                />
                <MetricCard
                  label={`Long WR (≥ ${analytics.duration_analysis.median_duration_mins.toFixed(0)} min)`}
                  value={`${analytics.duration_analysis.long_trade_win_rate.toFixed(1)}%`}
                  positive={analytics.duration_analysis.long_trade_win_rate >= 50}
                  negative={analytics.duration_analysis.long_trade_win_rate < 50}
                />
                <MetricCard label="Ø Win Duration"  value={`${analytics.duration_analysis.avg_win_duration.toFixed(0)} min`}  positive />
                <MetricCard label="Ø Loss Duration" value={`${analytics.duration_analysis.avg_loss_duration.toFixed(0)} min`} negative />
              </div>

              {analytics.anomalies.length > 0 && (
                <>
                  <div className="section-title">⚡ Anomalien</div>
                  {analytics.anomalies.map((a, i) => (
                    <div key={i} className={`anomaly-card anomaly-${a.severity}`}>
                      <div className="anomaly-header">
                        <span className="anomaly-type">{a.anomaly_type.replace('_', ' ').toUpperCase()}</span>
                        <span className={`anomaly-sev sev-${a.severity}`}>{a.severity}</span>
                      </div>
                      <p>{a.description}</p>
                      <small>{a.detected_at} · {a.trade_count} Trades</small>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ══ ML & ALGO DISCOVERY ══ */}
      {tab === 'ml' && (
        <>
          {analyticsLoading && (
            <div className="loading-state"><div className="loading-spinner" /> ML Pattern Detection...</div>
          )}
          {!analyticsLoading && !algorithm && (
            <div className="empty-state"><p>Mindestens 5 Trades mit Session-Daten nötig.</p></div>
          )}
          {algorithm && (
            <>
              <div className="algo-card">
                <div className="algo-header">
                  <span className="algo-badge">🤖 Erkannter Algorithmus</span>
                  <span className="algo-conf">{algorithm.overall_confidence.toFixed(0)}% Konfidenz</span>
                </div>
                <p className="algo-style">{algorithm.estimated_algorithm}</p>
                <div className="algo-split">
                  <div className="algo-box">
                    <div className="algo-box-label text-green">✅ Primärer Edge</div>
                    <p>{algorithm.primary_edge}</p>
                  </div>
                  <div className="algo-box">
                    <div className="algo-box-label text-red">⚠ Primäre Schwäche</div>
                    <p>{algorithm.primary_weakness}</p>
                  </div>
                </div>
                <div className="algo-rec">
                  <strong>💡 Empfehlung:</strong> {algorithm.recommendation}
                </div>
              </div>

              <div className="section-title">
                🔍 Erkannte Patterns <span className="count-badge">{algorithm.detected_patterns.length}</span>
              </div>

              {algorithm.detected_patterns.map((p, i) => (
                <div key={i} className={`pattern-card pattern-${p.pattern_type}`}>
                  <div className="pattern-header">
                    <span className={`pt-type pt-${p.pattern_type}`}>
                      {p.pattern_type === 'edge' ? '🟢 Edge' : p.pattern_type === 'weakness' ? '🔴 Weakness' : '🟡 Neutral'}
                    </span>
                    <div className="conf-bar">
                      <div className="conf-fill" style={{ width: `${p.confidence_score}%` }} />
                      <span>{p.confidence_score.toFixed(0)}%</span>
                    </div>
                  </div>
                  <p className="pattern-desc">{p.description}</p>
                  <div className="pattern-conds">
                    {p.conditions.map((c, ci) => (
                      <span key={ci} className="cond-chip">{c.field} {c.operator} {c.value}</span>
                    ))}
                  </div>
                  <div className="pattern-stats">
                    {[
                      ['Win Rate', `${p.win_rate.toFixed(1)}%`, p.win_rate >= 50],
                      ['Avg RR',   p.avg_rr > 0 ? p.avg_rr.toFixed(2) : '—', null],
                      ['Sample',   `${p.sample_size}`, null],
                      ['PnL',      `$${p.total_pnl.toFixed(2)}`, p.total_pnl >= 0],
                    ].map(([lbl, val, pos], pi) => (
                      <div key={pi} className="pstat">
                        <span>{lbl as string}</span>
                        <strong className={pos === true ? 'text-green' : pos === false ? 'text-red' : ''}>
                          {val as string}
                        </strong>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {algorithm.detected_patterns.length > 0 && (
                <div className="chart-container">
                  <h2>Pattern Win Rate Vergleich</h2>
                  <ResponsiveContainer width="100%" height={Math.max(200, algorithm.detected_patterns.length * 50)}>
                    <BarChart data={algorithm.detected_patterns} layout="vertical" margin={{ left: 10, right: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 11 }} />
                      <YAxis type="category" dataKey="pattern_id" tick={{ fill: '#64748b', fontSize: 10 }} width={130} />
                      <Tooltip
                        contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
                        formatter={(v: any) => [`${Number(v).toFixed(1)}%`, 'Win Rate']}
                      />
                      <Bar dataKey="win_rate" radius={[0, 6, 6, 0]}>
                        {algorithm.detected_patterns.map((p, i) => (
                          <Cell key={i} fill={
                            p.pattern_type === 'edge' ? '#22c55e' :
                            p.pattern_type === 'weakness' ? '#ef4444' : '#f59e0b'
                          } />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;